import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, CheckCircle2, ArrowRight, ArrowLeft, Flame, Download, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import SyllabusModal from "@/components/SyllabusModal";

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function parsePriceINR(feeStr) {
  if (!feeStr) return null;
  const m = String(feeStr).replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
  return m ? Number(m[1]) : null;
}

function toEmbedUrl(url) {
  if (!url) return "";
  if (url.includes("youtu.be/")) {
    const id = url.split("youtu.be/")[1].split(/[?&]/)[0];
    return `https://www.youtube.com/embed/${id}`;
  }
  if (url.includes("youtube.com/watch")) {
    try { const u = new URL(url); return `https://www.youtube.com/embed/${u.searchParams.get("v")}`; } catch { return url; }
  }
  if (url.includes("vimeo.com/")) {
    const id = url.split("vimeo.com/")[1].split(/[?&]/)[0];
    return `https://player.vimeo.com/video/${id}`;
  }
  return url;
}

function ModuleAccordion({ modules }) {
  const [open, setOpen] = useState(0);
  if (!Array.isArray(modules) || modules.length === 0) return null;
  return (
    <div className="mt-6 space-y-3" data-testid="course-modules">
      {modules.map((m, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="border border-neutral-200 rounded-2xl overflow-hidden bg-white">
            <button
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-neutral-50 transition-colors"
              data-testid={`module-toggle-${i}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                <div>
                  <div className="font-display font-bold text-base">{m.title}</div>
                  {m.duration && <div className="text-xs text-neutral-500 mt-0.5">{m.duration}</div>}
                </div>
              </div>
              {isOpen ? <ChevronDown className="w-5 h-5 text-neutral-400" /> : <ChevronRight className="w-5 h-5 text-neutral-400" />}
            </button>
            {isOpen && Array.isArray(m.topics) && m.topics.length > 0 && (
              <div className="px-5 pb-5 pt-1 border-t border-neutral-100">
                <ul className="mt-3 space-y-2">
                  {m.topics.map((t, ti) => (
                    <li key={ti} className="flex items-start gap-2 text-sm text-neutral-700">
                      <CheckCircle2 className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function CourseDetailPage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [syllabusOpen, setSyllabusOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const { settings } = useSettings();

  useEffect(() => {
    api.get(`/courses/${slug}`).then(({ data }) => setCourse(data)).catch(() => setCourse(false));
  }, [slug]);

  // Inject per-course JSON-LD (schema:Course + BreadcrumbList)
  useEffect(() => {
    if (!course || !course.slug) return;
    if (settings?.seo?.structured_data_enabled === false) {
      upsertJsonLd("ld-course", null);
      upsertJsonLd("ld-breadcrumb", null);
      return;
    }
    const origin = window.location.origin;
    const url = `${origin}/courses/${course.slug}`;
    const providerId = `${origin}#organization`;
    const price = parsePriceINR(course.fees);
    const image = course.banner || course.thumbnail || settings?.seo?.og_image;

    const teaches = [
      ...(course.outcomes || []),
      ...(course.tools || []).slice(0, 12),
    ].slice(0, 20);

    upsertJsonLd("ld-course", {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": url,
      name: course.title,
      description: course.overview || course.description,
      url,
      image,
      inLanguage: "en",
      courseCode: course.slug,
      educationalLevel: course.category || "Professional",
      educationalCredentialAwarded: course.certification || undefined,
      coursePrerequisites: (course.target_audience || []).join(", ") || undefined,
      teaches: teaches.length ? teaches : undefined,
      provider: {
        "@type": "EducationalOrganization",
        "@id": providerId,
        name: settings?.site_name || "EQtech Academy",
        sameAs: origin,
      },
      offers: price != null ? {
        "@type": "Offer",
        price,
        priceCurrency: "INR",
        availability: (course.seats_left || 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
        url: course.purchase_url || settings?.student_portal?.signup || url,
        category: "Paid",
      } : undefined,
      hasCourseInstance: [{
        "@type": "CourseInstance",
        courseMode: (course.learning_mode || "Online").toLowerCase().includes("offline") ? "Onsite" : "Online",
        courseWorkload: course.duration || undefined,
        location: {
          "@type": "VirtualLocation",
          url: course.purchase_url || settings?.student_portal?.signup || url,
        },
      }],
    });
    upsertJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Courses", item: `${origin}/courses` },
        { "@type": "ListItem", position: 3, name: course.title, item: url },
      ],
    });

    return () => {
      upsertJsonLd("ld-course", null);
      upsertJsonLd("ld-breadcrumb", null);
    };
  }, [course, settings]);

  if (course === null) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading...</div>;
  if (course === false) return (
    <div className="min-h-[60vh] flex items-center justify-center flex-col gap-4">
      <div>Course not found</div>
      <Link to="/courses" className="btn-primary-eq">Browse courses</Link>
    </div>
  );

  const enrollUrl = course.purchase_url || settings?.student_portal?.signup || "https://course.eqtech.academy/signup.php";
  const hasCurriculum = (course.modules && course.modules.length > 0) || (course.curriculum && course.curriculum.length > 0);
  const hasTools = course.tools && course.tools.length > 0;
  const hasOutcomes = course.outcomes && course.outcomes.length > 0;
  const hasProjects = course.projects && course.projects.length > 0;

  const tabs = [
    { id: "overview", label: "Overview" },
    hasCurriculum && { id: "curriculum", label: "Course Curriculum" },
    hasTools && { id: "tools", label: "Tools" },
    hasOutcomes && { id: "outcomes", label: "Outcomes" },
    hasProjects && { id: "projects", label: "Projects" },
  ].filter(Boolean);

  return (
    <div className="pt-12 pb-24" data-testid="course-detail-page">
      <div className="container-eq">
        <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-black mb-6"><ArrowLeft className="w-4 h-4" /> All Courses</Link>
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-widest text-sky-500 font-semibold">{course.category}</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter mt-2" data-testid="course-title">{course.title}</h1>
            <p className="text-neutral-600 mt-2 text-lg">{course.subtitle}</p>
            {course.tagline && (
              <p className="mt-3 text-sm italic text-neutral-500" data-testid="course-tagline">"{course.tagline}"</p>
            )}

            {course.highlights?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="course-highlights">
                {course.highlights.map((h, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-100 text-xs font-semibold">
                    <Sparkles className="w-3 h-3" /> {h}
                  </span>
                ))}
              </div>
            )}

            {course.banner && <img src={course.banner} alt={course.title} className="mt-8 rounded-2xl w-full aspect-[16/9] object-cover" />}
            {!course.banner && course.thumbnail && <img src={course.thumbnail} alt={course.title} className="mt-8 rounded-2xl w-full aspect-[16/9] object-cover" />}

            {/* Mobile-only CTAs directly below banner */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 lg:hidden" data-testid="course-mobile-ctas">
              <a href={enrollUrl} target="_blank" rel="noreferrer"
                 className="btn-primary-eq w-full justify-center" data-testid="course-mobile-enroll">
                Enroll Now <ArrowRight className="w-4 h-4" />
              </a>
              {course.syllabus_pdf && (
                <button onClick={() => setSyllabusOpen(true)}
                        className="btn-outline-eq w-full justify-center" data-testid="course-mobile-syllabus">
                  <Download className="w-4 h-4" /> Syllabus Download
                </button>
              )}
            </div>

            {course.demo_video && (
              <div className="mt-8 rounded-2xl overflow-hidden bg-neutral-950 aspect-video" data-testid="course-demo-video">
                <iframe
                  src={toEmbedUrl(course.demo_video)}
                  title="Course demo"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            {/* Tabs */}
            <div className="mt-10 border-b border-neutral-200 flex flex-wrap gap-1" data-testid="course-tabs">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? "border-sky-500 text-sky-600" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}
                  data-testid={`course-tab-${t.id}`}
                >{t.label}</button>
              ))}
            </div>

            <div className="mt-6" data-testid={`course-tab-content-${tab}`}>
              {tab === "overview" && (
                <div>
                  <p className="text-neutral-700 leading-relaxed whitespace-pre-line">
                    {course.overview || course.description}
                  </p>
                  {course.target_audience?.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-display font-bold text-xl">Who is this for?</h3>
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {course.target_audience.map((a, i) => (
                          <div key={i} className="p-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-sky-500 shrink-0" /> {a}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {course.features?.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-display font-bold text-xl">Features</h3>
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {course.features.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm p-3 rounded-xl bg-sky-50 text-sky-900 border border-sky-100">
                            <CheckCircle2 className="w-4 h-4" /> {f}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {course.certification && (
                    <div className="mt-8 p-6 rounded-2xl bg-sky-50 border border-sky-100">
                      <div className="label-eq text-sky-500 mb-2">Certification</div>
                      <div className="text-sm text-neutral-700">{course.certification}</div>
                    </div>
                  )}
                </div>
              )}

              {tab === "curriculum" && (
                <div>
                  {course.modules?.length > 0 ? (
                    <>
                      <p className="text-sm text-neutral-600">A {course.modules.length}-module hands-on program. Click each module to expand.</p>
                      <ModuleAccordion modules={course.modules} />
                    </>
                  ) : (
                    <ul className="space-y-2">
                      {course.curriculum.map((m, i) => (
                        <li key={i} className="flex items-center gap-3 p-4 bg-white border border-neutral-200 rounded-xl">
                          <CheckCircle2 className="w-5 h-5 text-sky-500 shrink-0" />
                          <span>{m}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {tab === "tools" && (
                <div>
                  <p className="text-sm text-neutral-600 mb-4">You will get hands-on training with these industry-leading AI tools:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {course.tools.map((tl, i) => (
                      <div key={i} className="p-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm text-center font-medium text-neutral-700">
                        {tl}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tab === "outcomes" && (
                <div>
                  <p className="text-sm text-neutral-600 mb-4">By the end of this program, you will have:</p>
                  <ul className="space-y-2">
                    {course.outcomes.map((o, i) => (
                      <li key={i} className="flex items-start gap-3 p-4 bg-white border border-neutral-200 rounded-xl">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tab === "projects" && (
                <div>
                  <p className="text-sm text-neutral-600 mb-4">Real, industry-grade projects that build your portfolio:</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {course.projects.map((p, i) => (
                      <div key={i} className="p-4 rounded-xl bg-neutral-50 border border-neutral-200 text-sm">{p}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 card-eq p-6">
              {course.show_urgency && course.seats_left > 0 && (
                <div className="mb-4 -mx-6 -mt-6 px-6 py-3 bg-orange-500 text-white text-sm font-semibold flex items-center gap-2 rounded-t-2xl" data-testid="course-urgency">
                  <Flame className="w-4 h-4" /> Only {course.seats_left} seats left!
                </div>
              )}
              <div className="text-3xl font-display font-black">{course.fees}</div>
              <div className="text-sm text-neutral-500 mb-5">Total course fee</div>
              <div className="space-y-3 text-sm border-t border-neutral-100 pt-5">
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-sky-500" /> {course.duration}</div>
                {course.trainer && <div className="flex items-center gap-2"><User className="w-4 h-4 text-sky-500" /> {course.trainer}</div>}
              </div>
              <a href={enrollUrl} target="_blank" rel="noreferrer"
                 className="btn-primary-eq w-full justify-center mt-5" data-testid="course-detail-enroll">
                Enroll Now <ArrowRight className="w-4 h-4" />
              </a>
              {course.syllabus_pdf && (
                <button onClick={() => setSyllabusOpen(true)}
                        className="btn-outline-eq w-full justify-center mt-3" data-testid="course-detail-syllabus">
                  <Download className="w-4 h-4" /> Syllabus Download
                </button>
              )}
            </div>
          </aside>
        </div>
      </div>
      <SyllabusModal
        open={syllabusOpen}
        onClose={() => setSyllabusOpen(false)}
        courseSlug={course.slug}
        courseTitle={course.title}
        syllabusUrl={course.syllabus_pdf}
      />
    </div>
  );
}
